import { createServerFn } from "@tanstack/react-start";
import type { DiscoveryListingCard } from "@wags/discovery/index.ts";
import {
    type FindFeedInput,
    type FindFeedRowKey,
    findFeedInputSchema,
    findFeedRowKeys,
    type NearestDogsInput,
    nearestDogsInputSchema,
} from "@wags/discovery/index.ts";
import { findFeedRowDefinitions } from "@wags/discovery/pure.ts";
import { resolveViewerProfileIdentity } from "@/lib/core/auth";
import {
    type CompatibilityScoreSidecars,
    getCompatibilityScoreSidecarsForListings,
} from "@/features/matching";
import type { SearchParams } from "@/features/discovery";
import {
    getDiscovery,
    getDiscoveryEventEmitter,
} from "../../../lib/core/discovery-core.server.ts";
import {
    loadFindFeedCompatibilitySidecars,
    queryErrorDetails,
    type FindFeedListingDocuments,
} from "./find-feed-viewer-sidecars";

export { findFeedRowDefinitions, findFeedRowKeys, type FindFeedRowKey };

export type FindFeedRowFilters = Partial<
    Pick<
        SearchParams,
        | "friendlinessChildren"
        | "friendlinessDogs"
        | "friendlinessCats"
        | "trainability"
        | "energyLevel"
        | "healthIssuesRisk"
        | "hypoallergenic"
        | "providerType"
    >
>;

export type FindFeedRowDefinition = {
    key: FindFeedRowKey;
    title: string;
    filters: FindFeedRowFilters;
};

export type FindFeedFeaturedListing = DiscoveryListingCard & {
    description: string;
};

export type FindFeedRow = FindFeedRowDefinition & {
    dogListings: DiscoveryListingCard[];
};

export type FindFeedResponse = {
    featuredListing: FindFeedFeaturedListing | null;
    listingRows: FindFeedRow[];
    listingDocuments?: FindFeedListingDocuments;
};

export type FindFeedBundledResponse = {
    result: FindFeedResponse;
    sidecars: {
        compatibilityScores: CompatibilityScoreSidecars;
    };
};

export type NearestDogResult = DiscoveryListingCard;

async function fetchFindFeedData(
    data: FindFeedInput,
): Promise<FindFeedResponse> {
    const discovery = await getDiscovery();
    return await discovery.feeds.findFeed(data);
}

async function fetchFindFeedBundledData(
    data: FindFeedInput,
): Promise<FindFeedBundledResponse> {
    const viewerPromise = resolveViewerProfileIdentity();
    const { listingDocuments, ...result } = await fetchFindFeedData({
        ...data,
        includeListingDocuments: true,
    });
    const { compatibilityScores, viewerError } =
        await loadFindFeedCompatibilitySidecars({
            listingDocuments,
            viewerPromise,
            loadSidecars: getCompatibilityScoreSidecarsForListings,
        });

    if (viewerError) {
        const telemetry = await getDiscoveryEventEmitter();
        telemetry.log.warn(
            "find_feed.viewer_identity_failed",
            queryErrorDetails(viewerError),
        );
    }

    return {
        result,
        sidecars: { compatibilityScores },
    };
}

export const getFindFeedWithCompatibilityScores = createServerFn({
    method: "GET",
})
    .inputValidator((data: FindFeedInput) => findFeedInputSchema.parse(data))
    .handler(async ({ data }) => await fetchFindFeedBundledData(data));

async function getNearestDogsData(data: NearestDogsInput) {
    const discovery = await getDiscovery();
    return await discovery.feeds.nearestDogs(data);
}

export const getNearestDogs = createServerFn({ method: "GET" })
    .inputValidator((data: NearestDogsInput) =>
        nearestDogsInputSchema.parse(data),
    )
    .handler(async ({ data }) => await getNearestDogsData(data));
